import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import HomePage from './pages/HomePage/HomePage';
import SingleProjectPage from './pages/SingleProjectPage/SingleProjectPage';
import ProjectsPage from './pages/ProjectsPage/ProjectsPage';
import Header from './components/Header/Header';
import TimelinePage from './pages/TimelinePage/TimelinePage';
import './App.css';
import CookieBanner from './components/CookieBanner/CookieBanner';

const API_TOKEN = process.env.REACT_APP_API_TOKEN;

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [isExitingSingleProject, setIsExitingSingleProject] = useState(false);
  const [isExitingTimeline, setIsExitingTimeline] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);
  const [prevLocation, setPrevLocation] = useState(location.pathname);
  const [isEntering, setIsEntering] = useState(false);
  const [enteringClass, setEnteringClass] = useState('');
  /* const [isScreenMedium, setIsScreenMedium] = useState(window.innerWidth <= 1280);
  const [isScreenSmall, setIsScreenSmall] = useState(window.innerWidth <= 800); */
  const [screenHeight, setScreenHeight] = useState('');
  const [screenSize, setScreenSize] = useState('');
  const [imageResize, setImageResize] = useState('');

  const [sliderTheme, setSliderTheme] = useState('Dark');

  // Função para lidar com a rolagem
  let lastScrollTop = 0;
  let scrollUpDistance = 0;

  useEffect(() => {
    const scrollDistance = 150; // Distância para mostrar/esconder o cabeçalho
    let lastScrollTop = 0;
    let scrollUpDistance = 0;
  
    const handleScroll = () => {
      const scrollTop = pageElement.scrollTop;
  
      if (scrollTop > lastScrollTop) {
        // Scrolling down
        setIsHeaderVisible(false);
        scrollUpDistance = 0; // Reset scrollUpDistance when scrolling down
      } else {
        // Scrolling up
        scrollUpDistance += lastScrollTop - scrollTop;
        if (scrollUpDistance > scrollDistance) {
          setIsHeaderVisible(true);
        }
      }
  
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop; // For Mobile or negative scrolling
    };
  
    const pageElement = document.querySelector('.page');
  
    if ((pageElement && location.pathname === '/projects') || (pageElement && location.pathname === '/timeline')) {
      pageElement.addEventListener('scroll', handleScroll);
    }
  
    return () => {
      if (pageElement) {
        pageElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [location.pathname]);
  

  useEffect(() => {
    const handleResize = () => {
      setScreenHeight(
        window.innerHeight <= 550 ? 'small' : '');
      
      setScreenSize(
        window.innerWidth <= 600 ? 'small' : 
        window.innerWidth <= 800 ? 'medium' : 
        window.innerWidth <= 1280 ? 'large' : 
        'full'
      );
  
      setImageResize(
        window.innerWidth <= 500 ? 'small' : 
        window.innerWidth <= 750 ? 'medium' : 
        window.innerWidth <= 100 ? 'large' : 
        'original'
      );
    };
  
    window.addEventListener('resize', handleResize);
  
    // Initial setting
    handleResize();
  
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  

  useEffect(() => {
    if (isExiting || isExitingSingleProject || isExitingTimeline) {
      let timeout = 600;
      if (isExiting) {
        timeout = 600;
      } else if (isExitingSingleProject) {
        timeout = 600;
      } else if (isExitingTimeline && screenSize === 'full') {
        timeout = 1200;
      } else if (isExitingTimeline && screenSize === 'large') {
        timeout = 600;
      }
      
      setTimeout(() => {
        setPrevLocation(location.pathname);
        navigate(nextLocation);
        setIsExiting(false);
        setIsExitingSingleProject(false);
        setIsExitingTimeline(false);
        setIsEntering(true);
        const enteringClass = getEnteringClassNames(location.pathname, nextLocation);
        setEnteringClass(enteringClass);
      }, timeout);
    }
  }, [isExiting, isExitingSingleProject, isExitingTimeline, navigate, nextLocation, location.pathname]);

  useEffect(() => {
    if (isEntering) {
      setTimeout(() => {
        setIsEntering(false);
      }, 600);
    }
  }, [isEntering]);

  const handleNavigation = (path) => {
    setIsHeaderVisible(true);
    setIsExiting(true);
    setNextLocation(path);
  };

  const handleSingleProjectNavigation = (path) => {
    setIsHeaderVisible(true);
    setIsExitingSingleProject(true);
    setNextLocation(path);
  };

  const handleTimelineNavigation = (path) => {
    setIsHeaderVisible(true);
    setIsExitingTimeline(true);
    setNextLocation(path);
  };

  const getClassNames = () => {
    const isProjectList = location.pathname === '/projects';
    const isProjectDetail = /^\/projects\/.+/.test(location.pathname);
    const isNextProjectList = nextLocation === '/projects';
    const isNextProjectDetail = /^\/projects\/.+/.test(nextLocation);

    if (location.pathname === '/' && isNextProjectList) {
      return 'homepage-to-project';
    } else if (location.pathname === '/' && nextLocation && isNextProjectDetail) {
      return 'homepage-to-single-project';
    } else if (location.pathname === '/timeline' && isNextProjectList) {
      return 'timeline-to-project';
    } else if (isProjectList && isNextProjectDetail) {
      return 'projects-to-project';
    } else if (isProjectDetail && isNextProjectDetail) {
      return 'project-to-project';
    } else {
      return 'default';
    }
  };

  const getEnteringClassNames = (fromPath, toPath) => {
    const fromClass = getClassFromPath(fromPath);
    const toClass = getClassFromPath(toPath);
    return `transition-${fromClass}-${toClass}`;
  };

  const getClassFromPath = (path) => {
    if (path === '/') return 'homepage';
    if (path === '/projects') return 'projects';
    if (path === '/timeline') return 'timeline';
    if (/^\/projects\/.+/.test(path)) return 'single-project';
    return 'default';
  };

  return (
    <div className={`App ${enteringClass}`}>
      <Header apiToken={API_TOKEN} onNavigate={handleNavigation} screenSize={screenSize} sliderTheme={sliderTheme} isHeaderVisible={isHeaderVisible} />
      <TransitionGroup component={null}>
        <CSSTransition
          key={location.key}
          timeout={{
            enter: isExitingSingleProject ? 400 : 600,
            exit: isExitingTimeline ? 1200 : isExitingSingleProject ? 400 : isExiting ? 600 : 0,
          }}
          classNames={getClassNames()}
          unmountOnExit
        >
          <div className={`page 
            ${isExiting || isExitingSingleProject ? getClassNames() : ''}
            ${isExiting ? 'isExiting' : ''} 
            ${isExitingSingleProject ? 'isExitingSingleProject' : ''}
            ${isExitingTimeline ? 'isExitingTimeline' : ''}
            ${isEntering ? 'isEntering' : ''}
            ${isEntering ? getEnteringClassNames() : ''}`}
          >
            <Routes location={location}>
              <Route
                path="/"
                element={<HomePage apiToken={API_TOKEN} onNavigate={handleNavigation} screenHeight={screenHeight} setSliderTheme={setSliderTheme} imageResize={imageResize} setIsHeaderVisible={setIsHeaderVisible} screenSize={screenSize}/>}
              />
              <Route
                path="/projects"
                element={<ProjectsPage apiToken={API_TOKEN} onNavigate={handleNavigation} setSliderTheme={setSliderTheme} imageResize={imageResize} isHeaderVisible={isHeaderVisible} setIsHeaderVisible={setIsHeaderVisible}/>}
              />
              <Route
                path="/timeline"
                element={<TimelinePage apiToken={API_TOKEN} onNavigate={handleNavigation} onLongNavigate={handleTimelineNavigation} setSliderTheme={setSliderTheme} setIsHeaderVisible={setIsHeaderVisible}/>}
              />
              <Route
                path="/projects/:slug"
                element={<SingleProjectPage apiToken={API_TOKEN} onNavigate={handleNavigation} onLongNavigate={handleSingleProjectNavigation} screenSize={screenSize} screenHeight={screenHeight} setSliderTheme={setSliderTheme} imageResize={imageResize} setIsHeaderVisible={setIsHeaderVisible}/>}
              />
            </Routes>
          </div>
        </CSSTransition>
      </TransitionGroup>
      <CookieBanner />
    </div>
  );
};

const Root = () => (
  <Router>
    <App />
  </Router>
);

export default Root;
